import React, { useState, useEffect } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollDirection } from './hooks/useScrollDirection';

const AnimatedNavbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { visible } = useScrollDirection();
  const [hasScrolled, setHasScrolled] = useState(false);
  
  // Check if page has scrolled to add background
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const navbarVariants = {
    visible: { 
      y: 0,
      opacity: 1,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 20,
        duration: 0.3
      }
    },
    hidden: { 
      y: -100, 
      opacity: 0,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 20,
        duration: 0.3
      }
    }
  };

  return (
    <>
      <motion.nav
        variants={navbarVariants}
        initial="visible"
        animate={visible ? "visible" : "hidden"}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          hasScrolled
            ? 'bg-black/90 backdrop-blur-md shadow-lg py-4'
            : 'bg-black/50 py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 flex items-center justify-between">
          {/* Removed gradient effects that caused green tint */}

          {/* Logo */}
          <div className="flex items-center space-x-2">
            <motion.img
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              src="/Assets/WEBSITE LOGO WMMN-1.png"
              alt="Logo"
              className="h-12 sm:h-16 md:h-18 w-auto"
            />
          </div>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center space-x-10">
            <NavLink href="#about">About</NavLink>
            <NavLink href="#services">Services</NavLink>
            <NavLink href="#media">Media</NavLink>
            
            {/* Flags */}
            <div className="flex items-center space-x-3 ml-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="hover:opacity-80 transition-opacity"
                title="Gibraltar"
              >
                <img 
                  src="/Assets/Flag_of_Gibraltar.svg" 
                  alt="Gibraltar" 
                  className="w-8 h-6 object-cover rounded shadow-md"
                />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="hover:opacity-80 transition-opacity"
                title="United States"
              >
                <img 
                  src="/Assets/usa-flag.jpg" 
                  alt="USA" 
                  className="w-8 h-6 object-cover rounded shadow-md"
                />
              </motion.button>
            </div>
          </div>

          {/* Mobile Menu Toggle */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="md:hidden text-white focus:outline-none z-20 p-2"
            onClick={toggleMobileMenu}
          >
            {mobileMenuOpen ? (
              <FiX className="h-7 w-7" />
            ) : (
              <FiMenu className="h-7 w-7" />
            )}
          </motion.button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/95 backdrop-blur-sm z-40 md:hidden pt-20"
          >
            <div className="flex flex-col items-center justify-center h-full space-y-8 text-lg">
              <MobileNavLink href="#about" onClick={() => setMobileMenuOpen(false)}>
                About
              </MobileNavLink>
              <MobileNavLink href="#services" onClick={() => setMobileMenuOpen(false)}>
                Services
              </MobileNavLink>
              <MobileNavLink href="#media" onClick={() => setMobileMenuOpen(false)}>
                Media
              </MobileNavLink>
              
              {/* Flags */}
              <div className="flex items-center space-x-6 mt-8">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  className="hover:opacity-80 transition-opacity"
                  onClick={() => setMobileMenuOpen(false)}
                  title="Gibraltar"
                >
                  <img 
                    src="/Assets/Flag_of_Gibraltar.svg" 
                    alt="Gibraltar" 
                    className="w-16 h-12 object-cover rounded-lg shadow-lg"
                  />
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  className="hover:opacity-80 transition-opacity"
                  onClick={() => setMobileMenuOpen(false)}
                  title="United States"
                >
                  <img 
                    src="/Assets/usa-flag.jpg" 
                    alt="USA" 
                    className="w-16 h-12 object-cover rounded-lg shadow-lg"
                  />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Desktop Navigation Link
const NavLink = ({ href, children }) => (
  <motion.a
    href={href}
    className="text-white/90 hover:text-white relative text-base font-medium tracking-wide"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    {children}
    <motion.span
      className="absolute bottom-0 left-0 w-0 h-[2px] bg-gradient-to-r from-blue-400 to-indigo-500"
      whileHover={{ width: '100%' }}
      transition={{ duration: 0.1 }}
    />
  </motion.a>
);

// Mobile Navigation Link
const MobileNavLink = ({ href, onClick, children }) => (
  <motion.a
    href={href}
    className="text-white/90 hover:text-blue-400 transition-colors text-3xl font-light"
    onClick={onClick}
    whileTap={{ scale: 0.95 }}
  >
    {children}
  </motion.a>
);

export default AnimatedNavbar; 